// src/pages/ModeratorSubjectsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import './ModeratorSubjectsPage.css';

export default function ModeratorSubjectsPage() {
  const locale = localStorage.getItem('locale') || 'kz';

  const t = useMemo(() => ({
    kz: {
      title: 'Пәндер',
      subtitle: 'Пәндер тізімін басқару',
      add: 'Қосу',
      edit: 'Өңдеу',
      save: 'Сақтау',
      delete: 'Жою',
      search: 'Іздеу...',
      id: 'ID',
      name_kz: 'Қазақша атауы',
      name_ru: 'Орысша атауы',
      action: 'Әрекет',
      addTitle: 'Пән қосу',
      editTitle: 'Пәнді өңдеу',
      required: 'Екі тілдегі атауларды толтырыңыз',
      error: 'Қате орын алды',
      empty: 'Дерек табылмады',
      rowsPerPage: 'Көрсету',
      page: 'Бет',
      sort: 'Сұрыптау',
      sortIdAsc: 'ID ↑',
      sortIdDesc: 'ID ↓',
      sortKzAsc: 'Қаз ↑',
      sortKzDesc: 'Қаз ↓',
      sortRuAsc: 'Рус ↑',
      sortRuDesc: 'Рус ↓',
      confirmDelete: 'Осы пәнді жоюға сенімдісіз бе?',
      deleteNotImplemented: 'Жою API кейін қосылады (endpoint жіберіңіз).'
    },
    ru: {
      title: 'Предметы',
      subtitle: 'Управление списком предметов',
      add: 'Добавить',
      edit: 'Редактировать',
      save: 'Сохранить',
      delete: 'Удалить',
      search: 'Поиск...',
      id: 'ID',
      name_kz: 'Название (каз)',
      name_ru: 'Название (рус)',
      action: 'Действие',
      addTitle: 'Добавить предмет',
      editTitle: 'Редактировать предмет',
      required: 'Заполните названия на двух языках',
      error: 'Произошла ошибка',
      empty: 'Ничего не найдено',
      rowsPerPage: 'Показать',
      page: 'Стр.',
      sort: 'Сортировка',
      sortIdAsc: 'ID ↑',
      sortIdDesc: 'ID ↓',
      sortKzAsc: 'Каз ↑',
      sortKzDesc: 'Каз ↓',
      sortRuAsc: 'Рус ↑',
      sortRuDesc: 'Рус ↓',
      confirmDelete: 'Вы уверены, что хотите удалить предмет?',
      deleteNotImplemented: 'API удаления добавим позже (скинь endpoint).'
    }
  })[locale], [locale]);

  // ===== data =====
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===== ui state =====
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortDir, setSortDir] = useState('asc');

  // pagination (client)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name_kz: '', name_ru: '' });

  useEffect(() => {
    fetchSubjects();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, sortKey, sortDir, pageSize]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    if (modalOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalOpen]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/backoffice/ref/subjects');
      setSubjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
    
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({ name_kz: '', name_ru: '' });
    setEditingId(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = (subject) => {
    setFormData({ name_kz: subject?.name_kz || '', name_ru: subject?.name_ru || '' });
    setEditingId(subject?.id ?? null);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name_kz?.trim() || !formData.name_ru?.trim()) {
      alert(t.required);
      return;
    }

    try {
      if (isEditing) {
        await axios.put(`/backoffice/ref/subjects/${editingId}`, {
          name_kz: formData.name_kz.trim(),
          name_ru: formData.name_ru.trim(),
        });
      } else {
        await axios.post('/backoffice/ref/subjects', {
          name_kz: formData.name_kz.trim(),
          name_ru: formData.name_ru.trim(),
        });
      }

      await fetchSubjects();
      setModalOpen(false);
    } catch (err) {
     
      alert(t.error);
    }
  };

  const handleDelete = async () => {
  if (!isEditing || !editingId) return;

  const ok = window.confirm(t.confirmDelete);
  if (!ok) return;

  try {
    await axios.delete(`/backoffice/ref/subjects/${editingId}`);

    await fetchSubjects();
    setModalOpen(false);
  } catch (err) {
   
    alert(t.error);
  }
};


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = subjects;

    if (q) {
      list = list.filter((s) => {
        const kz = (s.name_kz || '').toLowerCase();
        const ru = (s.name_ru || '').toLowerCase();
        return kz.includes(q) || ru.includes(q) || String(s.id || '').includes(q);
      });
    }

    return [...list].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'id') return (Number(a.id) - Number(b.id)) * dir;

      const av = String(a[sortKey] || '').toLowerCase();
      const bv = String(b[sortKey] || '').toLowerCase();
      return av.localeCompare(bv) * dir;
    });
  }, [subjects, query, sortKey, sortDir]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const sortOptions = [
    { key: 'id', dir: 'asc', label: t.sortIdAsc },
    { key: 'id', dir: 'desc', label: t.sortIdDesc },
    { key: 'name_kz', dir: 'asc', label: t.sortKzAsc },
    { key: 'name_kz', dir: 'desc', label: t.sortKzDesc },
    { key: 'name_ru', dir: 'asc', label: t.sortRuAsc },
    { key: 'name_ru', dir: 'desc', label: t.sortRuDesc },
  ];

  return (
    <div className="ms-page">
      <div className="ms-header">
        <div className="ms-titleBlock">
          <div className="ms-titleRow">
            <span className="ms-mark" />
            <h1 className="ms-title">{t.title}</h1>
          </div>
          <div className="ms-subtitle">{t.subtitle}</div>
        </div>

        <button className="ms-btn ms-btnPrimary" onClick={openAddModal}>
          <span className="ms-btnIcon">＋</span>
          {t.add}
        </button>
      </div>

      <div className="ms-card">
        <div className="ms-toolbar">
          <div className="ms-tools ms-toolsFull">
            <div className="ms-search">
              <span className="ms-searchIcon">🔎</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="ms-searchInput"
                placeholder={t.search}
              />
            </div>

            <select
              className="ms-select"
              value={`${sortKey}:${sortDir}`}
              onChange={(e) => {
                const [k, d] = e.target.value.split(':');
                setSortKey(k);
                setSortDir(d);
              }}
              aria-label={t.sort}
            >
              {sortOptions.map((o) => (
                <option key={`${o.key}:${o.dir}`} value={`${o.key}:${o.dir}`}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="ms-tableWrap">
          <table className="ms-table">
            <thead>
              <tr>
                <th className="ms-colId">{t.id}</th>
                <th>{t.name_kz}</th>
                <th>{t.name_ru}</th>
                <th className="ms-colAction">{t.action}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="ms-skeletonRow">
                    <td><div className="ms-skeleton" /></td>
                    <td><div className="ms-skeleton" /></td>
                    <td><div className="ms-skeleton" /></td>
                    <td><div className="ms-skeleton" /></td>
                  </tr>
                ))
              ) : pageItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="ms-empty">{t.empty}</td>
                </tr>
              ) : (
                pageItems.map((s) => (
                  <tr key={s.id}>
                    <td className="ms-colId">{s.id}</td>
                    <td className="ms-tdMain">{s.name_kz}</td>
                    <td className="ms-tdMain">{s.name_ru}</td>
                    <td className="ms-colAction">
                      <button className="ms-btn ms-btnSoft" onClick={() => openEditModal(s)}>
                        {t.edit}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="ms-cards">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="ms-cardRow ms-skeletonCard">
                  <div className="ms-skeleton" />
                  <div className="ms-skeleton" />
                  <div className="ms-skeleton" />
                </div>
              ))
            ) : pageItems.length === 0 ? (
              <div className="ms-emptyCard">{t.empty}</div>
            ) : (
              pageItems.map((s) => (
                <div key={s.id} className="ms-cardRow">
                  <div className="ms-cardTop">
                    <div className="ms-cardTitle">{s.name_kz}</div>
                    <div className="ms-badge">ID: {s.id}</div>
                  </div>
                  <div className="ms-cardMeta">
                    <div><span>Қаз:</span> {s.name_kz}</div>
                    <div><span>Рус:</span> {s.name_ru}</div>
                  </div>
                  <div className="ms-cardActions">
                    <button className="ms-btn ms-btnSoft" onClick={() => openEditModal(s)}>
                      {t.edit}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ms-footer">
          <div className="ms-left">
            <span className="ms-muted">{t.rowsPerPage}:</span>
            <select
              className="ms-select ms-selectSmall"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>

            <span className="ms-muted ms-range">
              {total === 0 ? '0' : ((safePage - 1) * pageSize + 1)}–{Math.min(safePage * pageSize, total)} / {total}
            </span>
          </div>

          <div className="ms-right">
            <button
              className="ms-btn ms-btnGhost"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹
            </button>

            <div className="ms-pagePill">
              {t.page} <b>{safePage}</b> / {totalPages}
            </div>

            <button
              className="ms-btn ms-btnGhost"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="ms-modalOverlay" onClick={() => setModalOpen(false)} role="dialog" aria-modal="true">
          <div className="ms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ms-modalHeader">
              <div className="ms-modalTitle">
                {isEditing ? t.editTitle : t.addTitle}
              </div>

              {/* Х — единственная кнопка закрытия */}
              <button className="ms-iconBtn" onClick={() => setModalOpen(false)} aria-label="close">
                ✕
              </button>
            </div>

            <div className="ms-modalBody">
              <div className="ms-field">
                <label className="ms-label">{t.name_kz}</label>
                <input
                  className="ms-input"
                  value={formData.name_kz}
                  onChange={(e) => setFormData((p) => ({ ...p, name_kz: e.target.value }))}
                />
              </div>

              <div className="ms-field">
                <label className="ms-label">{t.name_ru}</label>
                <input
                  className="ms-input"
                  value={formData.name_ru}
                  onChange={(e) => setFormData((p) => ({ ...p, name_ru: e.target.value }))}
                />
              </div>
            </div>

            <div className="ms-modalFooter">
              {/* Удалить — только при редактировании */}
              {isEditing && (
                <button className="ms-btn ms-btnDanger" onClick={handleDelete}>
                  {t.delete}
                </button>
              )}

              <button className="ms-btn ms-btnPrimary" onClick={handleSubmit}>
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
